"use client";

import { useEffect } from "react";
import { isEditableElement } from "../helpers/isEditableElement";

type Handler = (event: {
  event: ClipboardEvent;
  getData: (mimeType: string) => File | undefined;
  getText: () => string | undefined;
}) => void;

/**
 * usePaste is a custom hook that listens for paste events and allows you to handle pasted content.
 * The handler function is provided with helper functions to retrieve pasted data from the clipboard in a normalized way.
 * @param handler - A function that will be called when a paste event occurs. Make sure to wrap your handler in useCallback to avoid unnecessary re-renders.
 * @param skip Skip the paste handling
 */
export default function usePaste(handler: Handler, skip: boolean = false) {
  useEffect(() => {
    function handlePaste(event: ClipboardEvent) {
      /**
       * If the target is an input element, ignore the paste
       */
      if (skip || isEditableElement(event.target)) {
        return;
      }

      const getData = (mimeType: string) => {
        if (event.clipboardData?.items) {
          for (const item of Array.from(event.clipboardData.items)) {
            if (item.type === mimeType) {
              const blob = item.getAsFile();
              if (!blob) continue;
              let extension = mimeType.split("/")[1];
              if (extension.includes("+")) extension = extension.split("+")[0];
              return new File([blob], `pasted-image.${extension}`, { type: mimeType });
            }
          }
        }
      };

      const getText = () => event.clipboardData?.getData("text");

      handler({ event, getData, getText });
    }

    if (skip) return;

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [skip, handler]);
}
